import * as THREE from 'three'

/**
 * Generate planar UVs for any mesh in the given object that lacks a `uv`
 * attribute. Uses box (tri-planar) projection: each vertex is projected onto
 * the plane perpendicular to the dominant axis of its normal, so each face of
 * a box mesh receives its own independent [0, 1] UV space. Idempotent.
 */
export const ensurePlanarUVs = (object: THREE.Object3D): void => {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return
    const geometry = child.geometry as THREE.BufferGeometry
    if (geometry.attributes.uv || !geometry.attributes.position) return
    geometry.computeBoundingBox()
    const bbox = geometry.boundingBox
    if (!bbox) return

    if (!geometry.attributes.normal) {
      geometry.computeVertexNormals()
    }

    const sizeX = bbox.max.x - bbox.min.x || 1
    const sizeY = bbox.max.y - bbox.min.y || 1
    const sizeZ = bbox.max.z - bbox.min.z || 1
    const pos = geometry.attributes.position
    const normal = geometry.attributes.normal
    const uvs = new Float32Array(pos.count * 2)

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i)
      const y = pos.getY(i)
      const z = pos.getZ(i)
      const nx = Math.abs(normal.getX(i))
      const ny = Math.abs(normal.getY(i))
      const nz = Math.abs(normal.getZ(i))
      let u = 0
      let v = 0
      if (nx >= ny && nx >= nz) {
        u = (z - bbox.min.z) / sizeZ
        v = (y - bbox.min.y) / sizeY
      } else if (ny >= nx && ny >= nz) {
        u = (x - bbox.min.x) / sizeX
        v = (z - bbox.min.z) / sizeZ
      } else {
        u = (x - bbox.min.x) / sizeX
        v = (y - bbox.min.y) / sizeY
      }
      uvs[i * 2] = u
      uvs[i * 2 + 1] = v
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  })
}
